// Resolves which product surface the user is standing on from the SPA route.
//
// The media endpoints are shared: Editor's scene image/video, Storyboard's shot
// generation, Shot List's image generation and AI Video all POST to the same
// `video/*` routes. The route cannot know which of them called it, so the
// client says. Sending it from one place — api-request's makeRequest — rather
// than threading a surface argument through every atom, hook and modal is the
// same reasoning as route-script-uuid.js: the answer is a property of where the
// user is, so resolve it once instead of at every call site.
//
// Values must match the canonical list in
// packages/shared/src/common/ai-usage/features.js. The API validates against it
// and falls back to the endpoint's own feature on anything it does not
// recognise, so a name that drifts degrades rather than corrupting the ledger.

// Ordered: the first prefix that matches wins, so the specific editor surfaces
// have to precede the bare `/editor/:slug` screenplay editor they sit under.
//
// `breakdown` stays bare here even though the breakdown routes themselves now
// split into `breakdown:automatic` / `breakdown:manual` (#1426 task 9). That
// split is about *how a script got broken down*, and a route prefix cannot
// answer it: what this table labels is a shared media endpoint (scene image,
// scene video) called while the user happens to be standing on the breakdown
// screen, which is neither. Guessing a sub-feature there would put image spend
// under a name that reads as breakdown extraction; the bare base name is the
// honest answer and still rolls up with both halves.
const FEATURE_BY_PREFIX = [
  ['/editor/storyboard', 'storyboard'],
  ['/editor/break-smart', 'breakdown'],
  ['/breakdown', 'breakdown'],
  ['/editor', 'editor'],
  ['/shot-list', 'shotList'],
  ['/video-editor', 'aiVideo'],
  ['/scheduling', 'scheduling'],
  ['/budgeting-forecast', 'budgeting'],
  ['/pitch-deck', 'pitchDeck'],
  ['/plot-hole-report', 'plotHole'],
  ['/script-coverage', 'scriptCoverage'],
  ['/create-script', 'projectCreation'],
  ['/projects/new', 'projectCreation']
]

// The writers room names the surface in its third segment
// (/shared_project/:projectSlug/storyboard/:scriptUuid), so it cannot be
// matched by prefix.
const FEATURE_BY_SHARED_SEGMENT = {
  editor: 'editor',
  storyboard: 'storyboard',
  breakdown: 'breakdown',
  scheduling: 'scheduling',
  'budgeting-forecast': 'budgeting',
  'pitch-deck': 'pitchDeck'
}

const matchesPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

const sharedProjectFeature = (pathname) => {
  const segments = pathname.split('/').filter(Boolean)
  // [shared_project, :projectSlug, <surface>, ...]. The three-segment form is
  // /shared_project/:projectSlug/:featureName — the project hub, which
  // generates nothing — so it deliberately does not match.
  if (segments.length < 4) return null
  return FEATURE_BY_SHARED_SEGMENT[segments[2]] || null
}

// Returns null for any route with no product feature (dashboard, profile,
// workspace settings, the project hub, box office). Guessing would be worse
// than saying nothing: the endpoint's own feature is the right answer then.
export const resolveRouteFeature = (pathname) => {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null
  const normalised = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

  if (matchesPrefix(normalised, '/shared_project')) return sharedProjectFeature(normalised)

  const match = FEATURE_BY_PREFIX.find(([prefix]) => matchesPrefix(normalised, prefix))
  return match ? match[1] : null
}

export const currentRouteFeature = () => {
  // eslint-disable-next-line no-undef
  if (typeof window === 'undefined') return null
  // eslint-disable-next-line no-undef
  return resolveRouteFeature(window.location?.pathname)
}

// The name failures on this route are alerted under. Kept apart from the
// header resolver: the project hub must still send no x-nolan-feature (its
// co-pilot spend is `editor`), but its failures need a name.
export const resolveAlertFeature = (pathname) => {
  const feature = resolveRouteFeature(pathname)
  if (feature || typeof pathname !== 'string') return feature
  const hub = ['/project', '/shared_project'].some(prefix => matchesPrefix(pathname.replace(/\/+$/, ''), prefix))
  return hub ? 'project' : null
}

export const currentAlertFeature = () => {
  // eslint-disable-next-line no-undef
  if (typeof window === 'undefined') return null
  // eslint-disable-next-line no-undef
  return resolveAlertFeature(window.location?.pathname)
}
